import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import "./Dashboard.css";

const API_BASE = process.env.REACT_APP_API_URL;
const ORG_ID = "resh-dev";

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    OrgId: ORG_ID,
    "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

const formatCurrency = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const parseDate = (d) => {
  if (!d) return null;
  if (typeof d === "string") return new Date(d);
  if (d.$date) return new Date(typeof d.$date === "string" ? d.$date : d.$date);
  return null;
};

const getPaymentStatus = (booking) => {
  const payments = booking?.paymentsData;
  if (Array.isArray(payments) && payments.length > 0) {
    const sorted = [...payments].sort(
      (a, b) => new Date(b.payment_date || b.created_on || 0) - new Date(a.payment_date || a.created_on || 0)
    );
    return sorted[0].payment_status || "Not Paid";
  }
  return "Not Paid";
};

const getTotalPaid = (booking) => {
  const payments = booking?.paymentsData;
  if (!Array.isArray(payments)) return 0;
  return payments.reduce((s, p) => s + Number(p.amount || 0), 0);
};

// ── Skeleton ────────────────────────────────────────────────────────────────
function Skeleton({ width = "100%", height = "1rem", radius = "4px", style = {} }) {
  return (
    <div
      className="dash-skeleton"
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

function StatCardSkeleton() {
  return (
    <div className="dash-stat dash-stat--skeleton">
      <Skeleton width="36px" height="36px" radius="8px" />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <Skeleton width="60%" height="0.7rem" />
        <Skeleton width="40%" height="1.4rem" />
      </div>
    </div>
  );
}

// ── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, colorClass, onClick }) {
  return (
    <button
      className={`dash-stat dash-stat--${colorClass} ${onClick ? "dash-stat--clickable" : ""}`}
      onClick={onClick}
      type="button"
      title={onClick ? `View ${label}` : undefined}
    >
      <div className="dash-stat__icon-wrap">
        {icon}
      </div>
      <div className="dash-stat__body">
        <span className="dash-stat__label">{label}</span>
        <span className="dash-stat__value">{value}</span>
        {sub && <span className="dash-stat__sub">{sub}</span>}
      </div>
      {onClick && (
        <svg className="dash-stat__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      )}
    </button>
  );
}

// ── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ title, badge, action }) {
  return (
    <div className="dash-section-head">
      <div className="dash-section-head__left">
        <h3 className="dash-section-head__title">{title}</h3>
        {badge !== undefined && (
          <span className="dash-section-head__badge">{badge}</span>
        )}
      </div>
      {action && <div className="dash-section-head__action">{action}</div>}
    </div>
  );
}

// ── Donut Chart ──────────────────────────────────────────────────────────────
function DonutChart({ segments, size = 200 }) {
  const [hovered, setHovered] = useState(null);
  const r = 58;
  const cx = 80;
  const cy = 80;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((s, sg) => s + sg.value, 0) || 1;

  let cumulative = 0;
  const arcs = segments.map((sg) => {
    const frac = sg.value / total;
    const dash = Math.max(frac * circumference - 2, 0);
    const offset = circumference - cumulative * circumference;
    cumulative += frac;
    return { ...sg, dash, offset, frac };
  });

  const hovSeg = hovered !== null ? arcs[hovered] : null;

  return (
    <div className="dash-donut">
      <div className="dash-donut__svg-wrap" style={{ width: size, height: size }}>
        <svg width="100%" height="100%" viewBox="0 0 160 160">
          {arcs.map((arc, i) => (
            <circle
              key={i}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={arc.color}
              strokeWidth={hovered === i ? 24 : 20}
              strokeDasharray={`${arc.dash} ${circumference - arc.dash}`}
              strokeDashoffset={arc.offset}
              strokeLinecap="butt"
              transform={`rotate(-90 ${cx} ${cy})`}
              opacity={hovered === null ? 0.9 : hovered === i ? 1 : 0.3}
              style={{ cursor: "pointer", transition: "stroke-width 0.18s, opacity 0.18s" }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
          <text x={cx} y={cy - 9} textAnchor="middle" fontSize="22" fontWeight="700" fill="#111" fontFamily="Inter, sans-serif">
            {hovSeg ? hovSeg.value : total}
          </text>
          <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9" fill="#aaa" fontFamily="Inter, sans-serif" letterSpacing="0.08em">
            {hovSeg ? hovSeg.label.toUpperCase() : "TOTAL"}
          </text>
        </svg>
      </div>
      <div className="dash-donut__legend" style={{ width: "100%" }}>
        {arcs.map((arc, i) => {
          const pct = total > 0 ? Math.round((arc.value / total) * 100) : 0;
          return (
            <div
              key={arc.label}
              className={`dash-donut__legend-item ${hovered === i ? "dash-donut__legend-item--active" : ""}`}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{ display: "flex", flexDirection: "column", gap: "0.25rem", padding: "0.4rem 0.6rem", borderRadius: "6px", transition: "background-color 0.15s" }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.75rem", width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: arc.color, display: "inline-block" }} />
                  <span style={{ fontWeight: "500", color: "var(--color-text)" }}>{arc.label}</span>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", color: "var(--color-text-secondary)" }}>
                  <span style={{ fontWeight: "600" }}>{arc.value}</span>
                  <span style={{ color: "var(--color-text-muted)", fontSize: "0.7rem" }}>({pct}%)</span>
                </div>
              </div>
              <div style={{ width: "100%", height: "4px", background: "var(--color-background)", borderRadius: "2px", overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: arc.color, borderRadius: "2px", transition: "width 0.4s ease" }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Bar Chart ────────────────────────────────────────────────────────────────
function BarChart({ data, color = "#c9a96e", isCurrency = false }) {
  const [hovered, setHovered] = useState(null);
  const getValue = (d) => (isCurrency ? d.revenue : d.count) || 0;
  const max = Math.max(...data.map(getValue), 1);
  const barW = 22;
  const gap = 6;
  const chartH = 150;
  const totalW = data.length * (barW + gap) - gap;
  const svgH = chartH + 36;

  const fmt = (val) => {
    if (isCurrency) {
      if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
      if (val >= 1000) return `₹${Math.round(val / 1000)}k`;
      return `₹${val}`;
    }
    return val;
  };

  return (
    <div className="dash-bar-chart">
      <svg viewBox={`0 0 ${totalW} ${svgH}`} width="100%" style={{ overflow: "visible", display: "block" }}>
        {data.map((d, i) => {
          const val = getValue(d);
          const barH = Math.max(4, (val / max) * chartH);
          const x = i * (barW + gap);
          const y = chartH - barH;
          const isHov = hovered === i;
          return (
            <g key={i} style={{ cursor: "pointer" }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Hover area */}
              <rect x={x} y={0} width={barW} height={chartH} fill="transparent" />
              {/* Bar */}
              <rect x={x} y={y} width={barW} height={barH} rx="4"
                fill={color} opacity={isHov ? 1 : 0.65}
                style={{ transition: "opacity 0.15s" }}
              />
              {isHov && val > 0 && (
                <text x={x + barW / 2} y={y - 6} textAnchor="middle"
                  fontSize="9" fontWeight="700" fill={color} fontFamily="Inter, sans-serif">
                  {fmt(val)}
                </text>
              )}
              {/* X label */}
              <text x={x + barW / 2} y={chartH + 18} textAnchor="middle"
                fontSize="8" fill={isHov ? "#333" : "#bbb"}
                fontFamily="Inter, sans-serif"
                style={{ transition: "fill 0.15s" }}>
                {d.label ? d.label.replace(/ '\d{2}$/, "") : ""}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Line Chart ───────────────────────────────────────────────────────────────
function LineChart({ data, color = "#c9a96e", isCurrency = false }) {
  const [hovered, setHovered] = useState(null);
  const getValue = (d) => (isCurrency ? d.revenue : d.count) || 0;
  const max = Math.max(...data.map(getValue), 1);
  const w = 500;
  const h = 160;
  const padX = 12;
  const pts = data.map((d, i) => ({
    x: padX + (i / Math.max(data.length - 1, 1)) * (w - padX * 2),
    y: h - (getValue(d) / max) * (h - 20) - 10,
    val: getValue(d),
    label: d.label,
  }));

  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = pts.length > 0
    ? `${pathD} L ${pts[pts.length - 1].x} ${h} L ${pts[0].x} ${h} Z`
    : "";

  const fmt = (val) => {
    if (isCurrency) {
      if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
      if (val >= 1000) return `₹${Math.round(val / 1000)}k`;
      return `₹${val}`;
    }
    return val;
  };

  return (
    <div className="dash-line-chart">
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id={`lg-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Area fill */}
        {areaD && <path d={areaD} fill={`url(#lg-${color.replace("#", "")})`} />}
        {/* Line */}
        {pathD && <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
        {/* Dots */}
        {pts.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x} cy={p.y} r={hovered === i ? 6 : 4}
              fill={color} stroke="#fff" strokeWidth="2"
              style={{ cursor: "pointer", transition: "r 0.15s" }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
            {hovered === i && (
              <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="9" fontWeight="700"
                fill={color} fontFamily="Inter, sans-serif">
                {fmt(p.val)}
              </text>
            )}
          </g>
        ))}
        {/* X labels */}
        {pts.map((p, i) => (
          i % 2 === 0 && (
            <text key={i} x={p.x} y={h + 4} textAnchor="middle" fontSize="7.5" fill="#bbb" fontFamily="Inter, sans-serif">
              {p.label ? p.label.replace(/ '\d{2}$/, "") : ""}
            </text>
          )
        ))}
      </svg>
    </div>
  );
}

// ── Recent Booking Row ───────────────────────────────────────────────────────
function RecentBookingRow({ booking, onOpenDetails }) {
  const date = parseDate(booking.function_date);
  const dateStr = date
    ? date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
  const price = Number(booking.package_price || booking.amount || booking?.planeData?.price || 0);
  const paid = booking.total_paid != null ? Number(booking.total_paid) : getTotalPaid(booking);
  const due = Math.max(price - paid, 0);
  const payStatus = booking.payment_status || getPaymentStatus(booking);

  return (
    <tr
      className="dash-table__row dash-table__row--clickable"
      onClick={onOpenDetails}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenDetails(); } }}
    >
      <td className="dash-table__td">
        <div className="dash-table__name">{booking.client_name || "—"}</div>
        <div className="dash-table__phone">{booking.client_phone || ""}</div>
      </td>
      <td className="dash-table__td">
        <span className="dash-tag dash-tag--type">{booking.function_type || "—"}</span>
      </td>
      <td className="dash-table__td dash-table__td--date">{dateStr}</td>
      <td className="dash-table__td">
        <span className={`dash-tag dash-tag--status dash-tag--${(booking.status || "pending").toLowerCase()}`}>
          {booking.status || "Pending"}
        </span>
      </td>
      <td className="dash-table__td">
        <span className={`dash-tag dash-tag--pay dash-tag--${payStatus.replace(/\s/g, "-").toLowerCase()}`}>
          {payStatus}
        </span>
      </td>
      <td className="dash-table__td dash-table__td--amount">
        <div>{formatCurrency(price)}</div>
        {due > 0 && <div className="dash-table__due">Due: {formatCurrency(due)}</div>}
      </td>
    </tr>
  );
}

// ── Error Banner ─────────────────────────────────────────────────────────────
function ErrorBanner({ message, onRetry }) {
  return (
    <div className="dash-error-banner">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <span>{message}</span>
      {onRetry && <button type="button" className="dash-error-banner__retry" onClick={onRetry}>Retry</button>}
    </div>
  );
}

// ── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ message }) {
  return (
    <div className="dash-empty">
      <svg className="dash-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" width="40" height="40">
        <path d="M3 3h18v18H3z" rx="2" /><line x1="8" y1="12" x2="16" y2="12" />
      </svg>
      <p className="dash-empty__text">{message}</p>
    </div>
  );
}

// ── Upcoming Event Card ───────────────────────────────────────────────────────
function EventCard({ booking, onClick }) {
  const now = new Date();
  const d = parseDate(booking.function_date);
  const diff = d ? Math.ceil((d - now) / (1000 * 60 * 60 * 24)) : null;
  const price = Number(booking.package_price || booking.amount || 0);
  const paid = getTotalPaid(booking);
  const due = Math.max(price - paid, 0);
  const payStatus = getPaymentStatus(booking);

  return (
    <button type="button" className="dash-event-card" onClick={onClick}>
      <div className="dash-event-card__date">
        <span className="dash-event-card__day">{d ? d.getDate() : "--"}</span>
        <span className="dash-event-card__mon">{d ? d.toLocaleString("default", { month: "short" }) : "TBD"}</span>
      </div>
      <div className="dash-event-card__info">
        <span className="dash-event-card__name">{booking.client_name || "—"}</span>
        <span className="dash-event-card__type">{booking.function_type || "—"}</span>
        {booking.function_address && (
          <span className="dash-event-card__addr">{booking.function_address}</span>
        )}
      </div>
      <div className="dash-event-card__meta">
        <span className={`dash-event-card__countdown ${diff === 0 ? "dash-event-card__countdown--today" : ""}`}>
          {diff === 0 ? "Today" : diff != null ? `${Math.max(diff, 0)}d` : "—"}
        </span>
        <span className={`dash-tag dash-tag--pay dash-tag--${payStatus.replace(/\s/g, "-").toLowerCase()}`} style={{ fontSize: "0.6rem" }}>
          {payStatus}
        </span>
        {due > 0 && <span className="dash-event-card__due">Due {formatCurrency(due)}</span>}
      </div>
    </button>
  );
}

// ── Payment Follow-up Row ────────────────────────────────────────────────────
function PayFollowRow({ booking, onOpen }) {
  const date = parseDate(booking.function_date);
  const dateStr = date ? date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—";
  const price = Number(booking.package_price || booking.amount || booking?.planeData?.price || 0);
  const paid = getTotalPaid(booking);
  const due = Math.max(price - paid, 0);
  const payStatus = getPaymentStatus(booking);
  return (
    <tr className="dash-table__row dash-table__row--clickable" onClick={onOpen} tabIndex={0} role="button"
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}>
      <td className="dash-table__td">
        <div className="dash-table__name">{booking.client_name || "—"}</div>
        <div className="dash-table__phone">{booking.client_phone || ""}</div>
      </td>
      <td className="dash-table__td"><span className="dash-tag dash-tag--type">{booking.function_type || "—"}</span></td>
      <td className="dash-table__td dash-table__td--date">{dateStr}</td>
      <td className="dash-table__td">
        <span className={`dash-tag dash-tag--pay dash-tag--${payStatus.replace(/\s/g, "-").toLowerCase()}`}>{payStatus}</span>
      </td>
      <td className="dash-table__td dash-table__td--amount">
        <div>{formatCurrency(price)}</div>
        {due > 0 && <div className="dash-table__due">Due: {formatCurrency(due)}</div>}
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Dashboard
// ═══════════════════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const navigate = useNavigate();
  const adminName = localStorage.getItem("admin_name") || "Admin";
  const [paymentFollowupTab, setPaymentFollowupTab] = useState("not-paid");
  const [monthlyChartType, setMonthlyChartType] = useState("line"); // "line" | "bar"
  const [revenueChartType, setRevenueChartType] = useState("bar"); // "line" | "bar"

  // ── Per-widget state ────────────────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const [statusCounts, setStatusCounts] = useState([]);
  const [statusCountsLoading, setStatusCountsLoading] = useState(true);

  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(true);

  const [revenueTrend, setRevenueTrend] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(true);

  const [recentBookings, setRecentBookings] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState(null);

  // ── Legacy full bookings (for pending table, payment follow-ups, upcoming) ──
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);

  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [upcomingError, setUpcomingError] = useState(null);

  const [storageStats, setStorageStats] = useState(null);

  // ── Fetchers ─────────────────────────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await fetch(`${API_BASE}/dashboard/summary`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Failed to load summary");
      setSummary(data.data);
    } catch (err) {
      setSummaryError(err.message);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchStatusCounts = useCallback(async () => {
    setStatusCountsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/status-counts`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.status === 200) setStatusCounts(data.data || []);
    } catch {/* silent */ } finally {
      setStatusCountsLoading(false);
    }
  }, []);

  const fetchMonthlyTrend = useCallback(async () => {
    setMonthlyLoading(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/monthly-trend`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.status === 200) setMonthlyTrend(data.data || []);
    } catch {/* silent */ } finally {
      setMonthlyLoading(false);
    }
  }, []);

  const fetchRevenueTrend = useCallback(async () => {
    setRevenueLoading(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/revenue-trend`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.status === 200) setRevenueTrend(data.data || []);
    } catch {/* silent */ } finally {
      setRevenueLoading(false);
    }
  }, []);

  const fetchRecentBookings = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const res = await fetch(`${API_BASE}/dashboard/recent`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Failed to load recent bookings");
      setRecentBookings(data.data || []);
    } catch (err) {
      setRecentError(err.message);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  const fetchAllBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/dataset/data/getbookings`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ start: 0, end: 1000 }),
      });
      const data = await res.json();
      if (res.ok && data.status === 200) setBookings(data?.data?.[0]?.response ?? []);
    } catch {/* silent */ } finally {
      setBookingsLoading(false);
    }
  }, []);

  const fetchUpcomingEvents = useCallback(async () => {
    setUpcomingLoading(true);
    setUpcomingError(null);
    try {
      const res = await fetch(`${API_BASE}/schedule/upcoming-events?days=30&limit=8`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Failed to fetch upcoming events");
      setUpcomingEvents(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setUpcomingError(err.message);
      setUpcomingEvents([]);
    } finally {
      setUpcomingLoading(false);
    }
  }, []);

  const fetchStorageStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/gallery/storage`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.status === 200) {
        const stats = Array.isArray(data.data) ? data.data[0] : data.data;
        setStorageStats(stats);
      }
    } catch {/* silent */ }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchStatusCounts();
    fetchMonthlyTrend();
    fetchRevenueTrend();
    fetchRecentBookings();
    fetchAllBookings();
    fetchUpcomingEvents();
    fetchStorageStats();
  }, [fetchSummary, fetchStatusCounts, fetchMonthlyTrend, fetchRevenueTrend,
    fetchRecentBookings, fetchAllBookings, fetchUpcomingEvents, fetchStorageStats]);

  // ── Derived from all-bookings ─────────────────────────────────────────────
  const now = new Date();
  const pending = bookings.filter((b) => b.status === "Pending");
  const confirmed = bookings.filter((b) => b.status === "Confirmed");
  const unpaid = confirmed.filter((b) => getPaymentStatus(b) === "Not Paid");
  const advancePaid = confirmed.filter((b) => getPaymentStatus(b) === "Advance Paid");
  const pendingRows = pending.slice(0, 8);
  const paymentFollowupRows = (paymentFollowupTab === "advance-paid" ? advancePaid : unpaid).slice(0, 8);
  const paymentFollowupCount = paymentFollowupTab === "advance-paid" ? advancePaid.length : unpaid.length;

  const openBookingDetails = (bookingId) => navigate("/bookings", { state: { bookingId } });
  const navigateFiltered = (status) => navigate(`/bookings${status ? `?status=${encodeURIComponent(status)}` : ""}`);

  const today = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // Color map for status donut
  const STATUS_COLORS = {
    Pending: "#c9a96e",
    Confirmed: "#3b82f6",
    Completed: "#2d8a4e",
    Cancelled: "#d9534f",
  };

  const donutSegments = statusCounts.map((sc) => ({
    label: sc.status || "Unknown",
    value: sc.count,
    color: STATUS_COLORS[sc.status] || "#aaa",
  }));

  const totalRevenue = revenueTrend.reduce((s, m) => s + (m.revenue || 0), 0);

  return (
    <AdminLayout>
      <div className="dash">

        {/* ── Header ── */}
        <div className="dash__header">
          <div>
            <h1 className="dash__title">Dashboard</h1>
            <p className="dash__date">{today}</p>
          </div>
          <div className="dash__welcome">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ marginRight: "0.2rem", color: "var(--color-accent)" }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            <span>Welcome back, <strong>{adminName}</strong></span>
          </div>
        </div>

        {/* ── KPI Stat Cards ── */}
        <div>
          <div className="dash-section-label">Overview</div>
          {summaryError && <ErrorBanner message={summaryError} onRetry={fetchSummary} />}
          <div className="dash__stats">
            {summaryLoading ? (
              Array.from({ length: 7 }).map((_, i) => <StatCardSkeleton key={i} />)
            ) : summary ? (
              <>
                <StatCard
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>}
                  label="Total Bookings" value={summary.total} colorClass="total"
                  sub={`${summary.this_month} this month`}
                  onClick={() => navigateFiltered("")}
                />
                <StatCard
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>}
                  label="Pending" value={summary.pending} colorClass="pending"
                  sub="Awaiting confirmation"
                  onClick={() => navigateFiltered("Pending")}
                />
                <StatCard
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20"><polyline points="20 6 9 17 4 12" /></svg>}
                  label="Confirmed" value={summary.confirmed} colorClass="confirmed"
                  sub="Active bookings"
                  onClick={() => navigateFiltered("Confirmed")}
                />
                <StatCard
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>}
                  label="Completed" value={summary.completed} colorClass="completed"
                  sub="Successfully done"
                  onClick={() => navigateFiltered("Completed")}
                />
                <StatCard
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>}
                  label="Cancelled" value={summary.cancelled} colorClass="cancelled"
                  sub="Cancelled bookings"
                  onClick={() => navigateFiltered("Cancelled")}
                />
                <StatCard
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><circle cx="12" cy="16" r="1" fill="currentColor" /></svg>}
                  label="Today's Bookings" value={summary.today} colorClass="today"
                  sub="Function date today"
                  onClick={() => navigateFiltered("")}
                />
                <StatCard
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>}
                  label="This Month" value={summary.this_month} colorClass="month"
                  sub="Function date this month"
                  onClick={() => navigateFiltered("")}
                />
              </>
            ) : null}

            {/* Storage card */}
            {storageStats && (
              <button
                type="button"
                className="dash-stat dash-stat--storage dash-stat--clickable"
                onClick={() => navigate("/gallery")}
                title="View Gallery"
              >
                <div className="dash-stat__icon-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                    <path d="M21 10V4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6" />
                    <path d="M21 14v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6" />
                    <rect x="1" y="10" width="22" height="4" rx="1" />
                  </svg>
                </div>
                <div className="dash-stat__body" style={{ width: "100%" }}>
                  <span className="dash-stat__label">Cloud Storage</span>
                  <span className="dash-stat__value" style={{ fontSize: "1rem" }}>
                    {storageStats.used_gb.toFixed(2)} GB / {storageStats.total_gb.toFixed(0)} GB
                  </span>
                  <div className="dash-storage-bar">
                    <div className="dash-storage-bar__fill" style={{ width: `${storageStats.percent}%` }} />
                  </div>
                  <span className="dash-stat__sub" style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Used</span><span>{storageStats.percent.toFixed(1)}%</span>
                  </span>
                </div>
                <svg className="dash-stat__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Charts Row ── */}
        <div>
          <div className="dash-section-label">Analytics</div>
          <div className="dash__charts">

            {/* Status Donut */}
            <div className="dash__card dash__card--donut">
              <SectionHeader title="Booking Status" />
              {statusCountsLoading ? (
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", padding: "1rem 0" }}>
                  <Skeleton width="200px" height="200px" radius="50%" />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} height="0.8rem" width={`${70 - i * 10}%`} />)}
                  </div>
                </div>
              ) : donutSegments.length === 0 ? (
                <EmptyState message="No booking data yet" />
              ) : (
                <DonutChart segments={donutSegments} />
              )}
            </div>

            {/* Monthly Bookings Trend Chart */}
            <div className="dash__card dash__card--chart">
              <SectionHeader
                title="Monthly Bookings"
                badge={monthlyTrend.reduce((s, m) => s + (m.count || 0), 0)}
                action={
                  <div className="dash-chart-toggle" style={{ display: "inline-flex", gap: "0.25rem", background: "var(--color-background)", padding: "0.15rem", borderRadius: "4px" }}>
                    <button
                      type="button"
                      style={{
                        border: 0,
                        background: monthlyChartType === "line" ? "var(--color-secondary)" : "transparent",
                        color: monthlyChartType === "line" ? "var(--color-accent)" : "var(--color-text-muted)",
                        cursor: "pointer",
                        padding: "0.2rem 0.4rem",
                        borderRadius: "3px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: monthlyChartType === "line" ? "0 1px 3px rgba(0,0,0,0.05)" : "none"
                      }}
                      onClick={() => setMonthlyChartType("line")}
                      title="Line Chart"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><path d="M3 3v18h18" /><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" /></svg>
                    </button>
                    <button
                      type="button"
                      style={{
                        border: 0,
                        background: monthlyChartType === "bar" ? "var(--color-secondary)" : "transparent",
                        color: monthlyChartType === "bar" ? "var(--color-accent)" : "var(--color-text-muted)",
                        cursor: "pointer",
                        padding: "0.2rem 0.4rem",
                        borderRadius: "3px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: monthlyChartType === "bar" ? "0 1px 3px rgba(0,0,0,0.05)" : "none"
                      }}
                      onClick={() => setMonthlyChartType("bar")}
                      title="Bar Chart"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><path d="M3 3v18h18" /><path d="M7 16h3v5H7z" /><path d="M13 11h3v10h-3z" /><path d="M19 6h3v15h-3z" /></svg>
                    </button>
                  </div>
                }
              />
              {monthlyLoading ? (
                <Skeleton height="160px" style={{ marginTop: "1rem" }} />
              ) : monthlyTrend.length === 0 ? (
                <EmptyState message="No trend data" />
              ) : monthlyChartType === "line" ? (
                <LineChart data={monthlyTrend} color="#c9a96e" />
              ) : (
                <BarChart data={monthlyTrend} color="#c9a96e" />
              )}
            </div>

            {/* Revenue Trend Chart */}
            <div className="dash__card dash__card--chart">
              <SectionHeader
                title="Revenue Collected"
                badge={formatCurrency(totalRevenue)}
                action={
                  <div className="dash-chart-toggle" style={{ display: "inline-flex", gap: "0.25rem", background: "var(--color-background)", padding: "0.15rem", borderRadius: "4px" }}>
                    <button
                      type="button"
                      style={{
                        border: 0,
                        background: revenueChartType === "line" ? "var(--color-secondary)" : "transparent",
                        color: revenueChartType === "line" ? "#3b82f6" : "var(--color-text-muted)",
                        cursor: "pointer",
                        padding: "0.2rem 0.4rem",
                        borderRadius: "3px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: revenueChartType === "line" ? "0 1px 3px rgba(0,0,0,0.05)" : "none"
                      }}
                      onClick={() => setRevenueChartType("line")}
                      title="Line Chart"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><path d="M3 3v18h18" /><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" /></svg>
                    </button>
                    <button
                      type="button"
                      style={{
                        border: 0,
                        background: revenueChartType === "bar" ? "var(--color-secondary)" : "transparent",
                        color: revenueChartType === "bar" ? "#3b82f6" : "var(--color-text-muted)",
                        cursor: "pointer",
                        padding: "0.2rem 0.4rem",
                        borderRadius: "3px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: revenueChartType === "bar" ? "0 1px 3px rgba(0,0,0,0.05)" : "none"
                      }}
                      onClick={() => setRevenueChartType("bar")}
                      title="Bar Chart"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><path d="M3 3v18h18" /><path d="M7 16h3v5H7z" /><path d="M13 11h3v10h-3z" /><path d="M19 6h3v15h-3z" /></svg>
                    </button>
                  </div>
                }
              />
              {revenueLoading ? (
                <Skeleton height="150px" style={{ marginTop: "1rem" }} />
              ) : revenueTrend.length === 0 ? (
                <EmptyState message="No revenue data" />
              ) : revenueChartType === "line" ? (
                <LineChart data={revenueTrend} color="#3b82f6" isCurrency />
              ) : (
                <BarChart data={revenueTrend} color="#3b82f6" isCurrency />
              )}
            </div>

          </div>
        </div>

        {/* ── Recent Bookings Table ── */}
        <div className="dash__card">
          <SectionHeader
            title="Recent Bookings"
            action={
              <button className="dash-btn-link" onClick={() => navigate("/bookings")}>
                View All →
              </button>
            }
          />
          {recentError ? (
            <ErrorBanner message={recentError} onRetry={fetchRecentBookings} />
          ) : recentLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", padding: "0.5rem 0" }}>
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} height="2.5rem" />)}
            </div>
          ) : recentBookings.length === 0 ? (
            <EmptyState message="No bookings yet" />
          ) : (
            <div className="dash__table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th className="dash-table__th">Client</th>
                    <th className="dash-table__th">Type</th>
                    <th className="dash-table__th">Date</th>
                    <th className="dash-table__th">Status</th>
                    <th className="dash-table__th">Payment</th>
                    <th className="dash-table__th">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((b) => (
                    <RecentBookingRow
                      key={b._id}
                      booking={b}
                      onOpenDetails={() => openBookingDetails(b._id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Pending + Upcoming Row ── */}
        <div className="dash__main-row">
          {/* Pending Bookings */}
          <div className="dash__card">
            <SectionHeader
              title="Pending Bookings"
              badge={bookingsLoading ? "…" : pending.length}
              action={
                <button className="dash-btn-link" onClick={() => navigateFiltered("Pending")}>
                  View All →
                </button>
              }
            />
            {bookingsLoading ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.5rem 0" }}>
                {[1, 2, 3].map(i => <Skeleton key={i} height="2.2rem" />)}
              </div>
            ) : pendingRows.length === 0 ? (
              <EmptyState message="No pending bookings" />
            ) : (
              <div className="dash__table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th className="dash-table__th">Client</th>
                      <th className="dash-table__th">Type</th>
                      <th className="dash-table__th">Date</th>
                      <th className="dash-table__th">Status</th>
                      <th className="dash-table__th">Payment</th>
                      <th className="dash-table__th">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRows.map((b) => (
                      <RecentBookingRow
                        key={b._id}
                        booking={b}
                        onOpenDetails={() => openBookingDetails(b._id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Upcoming Events */}
          <div className="dash__card dash__card--upcoming">
            <SectionHeader
              title="Upcoming Events"
              badge={upcomingLoading ? "…" : upcomingEvents.length}
              action={
                <button className="dash-btn-link" onClick={() => navigate("/calendar")}>
                  Calendar →
                </button>
              }
            />
            {upcomingLoading ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", padding: "0.5rem 0" }}>
                {[1, 2, 3].map(i => <Skeleton key={i} height="5rem" />)}
              </div>
            ) : upcomingError ? (
              <ErrorBanner message={upcomingError} onRetry={fetchUpcomingEvents} />
            ) : upcomingEvents.length === 0 ? (
              <EmptyState message="No confirmed events in the next 30 days" />
            ) : (
              <div className="dash__events-grid">
                {upcomingEvents.map((b) => (
                  <EventCard key={b._id} booking={b} onClick={() => openBookingDetails(b._id)} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Payment Follow-ups ── */}
        <div className="dash__card">
          <SectionHeader
            title="Payment Follow-ups"
            badge={bookingsLoading ? "…" : paymentFollowupCount}
            action={
              <button className="dash-btn-link" onClick={() => navigateFiltered("Confirmed")}>
                View All →
              </button>
            }
          />
          <div className="dash-tabs" role="tablist">
            <button
              type="button"
              className={`dash-tabs__btn ${paymentFollowupTab === "not-paid" ? "dash-tabs__btn--active" : ""}`}
              onClick={() => setPaymentFollowupTab("not-paid")}
              role="tab" aria-selected={paymentFollowupTab === "not-paid"}
            >
              Not Paid <span>{unpaid.length}</span>
            </button>
            <button
              type="button"
              className={`dash-tabs__btn ${paymentFollowupTab === "advance-paid" ? "dash-tabs__btn--active" : ""}`}
              onClick={() => setPaymentFollowupTab("advance-paid")}
              role="tab" aria-selected={paymentFollowupTab === "advance-paid"}
            >
              Advance Paid <span>{advancePaid.length}</span>
            </button>
          </div>
          {bookingsLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.5rem 0" }}>
              {[1, 2, 3].map(i => <Skeleton key={i} height="2.2rem" />)}
            </div>
          ) : paymentFollowupRows.length === 0 ? (
            <EmptyState message={
              paymentFollowupTab === "advance-paid"
                ? "No confirmed bookings with advance payments"
                : "No confirmed unpaid bookings"
            } />
          ) : (
            <div className="dash__table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th className="dash-table__th">Client</th>
                    <th className="dash-table__th">Type</th>
                    <th className="dash-table__th">Date</th>
                    <th className="dash-table__th">Payment</th>
                    <th className="dash-table__th">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentFollowupRows.map((b) => (
                    <PayFollowRow key={b._id} booking={b} onOpen={() => openBookingDetails(b._id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Quick Links ── */}
        <div>
          <div className="dash-section-label">Quick Links</div>
          <div className="dash__quicklinks">
            {[
              {
                to: "/bookings",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ color: "var(--color-accent)" }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                ),
                label: "Manage Bookings",
                desc: "View, confirm & update all bookings"
              },
              {
                to: "/calendar",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ color: "var(--color-accent)" }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><path d="M8 14h8" /></svg>
                ),
                label: "Calendar",
                desc: "View scheduled sessions & events"
              },
              {
                to: "/gallery",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ color: "var(--color-accent)" }}><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                ),
                label: "Gallery",
                desc: "Manage photos & albums"
              },
              {
                to: "/screen-config",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ color: "var(--color-accent)" }}><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
                ),
                label: "Screen Config",
                desc: "Edit website content & sections"
              },
              {
                to: "/settings",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ color: "var(--color-accent)" }}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                ),
                label: "Settings",
                desc: "Configure org & app settings"
              },
            ].map((ql) => (
              <button key={ql.to} type="button" className="dash-ql" onClick={() => navigate(ql.to)}>
                <span className="dash-ql__icon">{ql.icon}</span>
                <div className="dash-ql__body">
                  <span className="dash-ql__label">{ql.label}</span>
                  <span className="dash-ql__desc">{ql.desc}</span>
                </div>
                <svg className="dash-ql__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}
